import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  FaSave,
  FaUndo,
  FaPlus,
  FaTrash,
  FaUpload,
  FaArrowUp,
  FaArrowDown,
} from 'react-icons/fa';
import { LuLayoutDashboard } from 'react-icons/lu';
import { useToast } from '../../../context/ToastContext';
import PageLayout from '../../../components/ui/PageLayout';
import PageHeader from '../../../components/ui/PageHeader';
import SkeletonLoader from '../../../components/ui/SkeletonLoader';
import PortalIconPicker from '../../../components/PortalCMS/PortalIconPicker';
import PortalColorPicker from '../../../components/PortalCMS/PortalColorPicker';
import {
  normalizeSocialLinksForEdit,
  SOCIAL_PRESETS,
  emptySocialLink,
} from '../../../utils/portal/socialUtils';

const LINK_TYPES = [
  { value: 'file', label: 'File download' },
  { value: 'internal', label: 'Internal link' },
  { value: 'external', label: 'External link' },
];

const emptyResource = (order) => ({
  title: '',
  description: '',
  icon: 'LuFileText',
  color: 'primary',
  linkType: 'file',
  fileUrl: '',
  downloadName: '',
  linkUrl: '',
  isActive: true,
  order,
});

const DashboardPortalCMS = () => {
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();
  const [activeTab, setActiveTab] = useState('header');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const [form, setForm] = useState({
    isPublished: true,
    welcomeTitle: '',
    welcomeIcon: 'LuSparkles',
    welcomeSubtitle: '',
    blogSectionTitle: '',
    blogSectionIcon: 'LuNewspaper',
    blogViewAllText: 'View All',
    blogEmptyTitle: 'No blog posts available',
    notificationsTitle: '',
    notificationsIcon: 'LuBell',
    notificationsEmptyTitle: 'No notifications yet',
    notificationsEmptySubtitle: "You'll see updates here when there's activity",
    whatsNewTitle: '',
    whatsNewIcon: 'LuRefreshCw',
    whatsNewSubtitle: '',
    connectTitle: '',
    connectIcon: 'LuRss',
    connectSubtitle: '',
    socialLinks: [],
    resources: [],
  });

  const fetchContent = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        const c = res.data.data.content || {};
        setForm({
          isPublished: res.data.data.isPublished !== false,
          welcomeTitle: c.welcomeTitle || '',
          welcomeIcon: c.welcomeIcon || 'LuSparkles',
          welcomeSubtitle: c.welcomeSubtitle || '',
          blogSectionTitle: c.blogSectionTitle || '',
          blogSectionIcon: c.blogSectionIcon || 'LuNewspaper',
          blogViewAllText: c.blogViewAllText || 'View All',
          blogEmptyTitle: c.blogEmptyTitle || 'No blog posts available',
          notificationsTitle: c.notificationsTitle || '',
          notificationsIcon: c.notificationsIcon || 'LuBell',
          notificationsEmptyTitle: c.notificationsEmptyTitle || 'No notifications yet',
          notificationsEmptySubtitle: c.notificationsEmptySubtitle || "You'll see updates here when there's activity",
          whatsNewTitle: c.whatsNewTitle || '',
          whatsNewIcon: c.whatsNewIcon || 'LuRefreshCw',
          whatsNewSubtitle: c.whatsNewSubtitle || '',
          connectTitle: c.connectTitle || '',
          connectIcon: c.connectIcon || 'LuRss',
          connectSubtitle: c.connectSubtitle || '',
          socialLinks: normalizeSocialLinksForEdit(c),
          resources: (c.resources || []).map((item, index) => ({ ...item, order: item.order ?? index })),
        });
      }
    } catch (err) {
      toastError('Failed to load dashboard portal content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const updateSocial = (index, field, value) => {
    setForm((prev) => {
      const socialLinks = [...prev.socialLinks];
      socialLinks[index] = { ...socialLinks[index], [field]: value };
      return { ...prev, socialLinks };
    });
  };

  const addSocialLink = (preset) => {
    setForm((prev) => ({
      ...prev,
      socialLinks: [...prev.socialLinks, emptySocialLink(prev.socialLinks.length, preset)],
    }));
  };

  const removeSocialLink = (index) => {
    if (!window.confirm('Remove this social platform?')) return;
    setForm((prev) => ({
      ...prev,
      socialLinks: prev.socialLinks.filter((_, i) => i !== index).map((item, i) => ({ ...item, order: i })),
    }));
  };

  const moveSocialLink = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= form.socialLinks.length) return;
    setForm((prev) => {
      const socialLinks = [...prev.socialLinks];
      [socialLinks[index], socialLinks[target]] = [socialLinks[target], socialLinks[index]];
      return { ...prev, socialLinks: socialLinks.map((item, i) => ({ ...item, order: i })) };
    });
  };

  const updateResource = (index, field, value) => {
    setForm((prev) => {
      const resources = [...prev.resources];
      resources[index] = { ...resources[index], [field]: value };
      return { ...prev, resources };
    });
  };

  const addResource = () => {
    setForm((prev) => ({
      ...prev,
      resources: [...prev.resources, emptyResource(prev.resources.length)],
    }));
  };

  const removeResource = (index) => {
    if (!window.confirm('Remove this resource card?')) return;
    setForm((prev) => ({
      ...prev,
      resources: prev.resources.filter((_, i) => i !== index).map((item, i) => ({ ...item, order: i })),
    }));
  };

  const moveResource = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= form.resources.length) return;
    setForm((prev) => {
      const resources = [...prev.resources];
      [resources[index], resources[target]] = [resources[target], resources[index]];
      return { ...prev, resources: resources.map((item, i) => ({ ...item, order: i })) };
    });
  };

  const uploadResourceFile = async (index, file) => {
    if (!file) return;
    setUploadingIndex(index);
    try {
      const token = localStorage.getItem('adminToken');
      const data = new FormData();
      data.append('file', file);
      const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/upload`, data, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) {
        updateResource(index, 'fileUrl', res.data.data.fileUrl);
        if (!form.resources[index]?.downloadName) {
          updateResource(index, 'downloadName', res.data.data.originalName);
        }
        toastSuccess('File uploaded');
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploadingIndex(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const payload = {
        isPublished: form.isPublished,
        content: {
          welcomeTitle: form.welcomeTitle,
          welcomeIcon: form.welcomeIcon,
          welcomeSubtitle: form.welcomeSubtitle,
          blogSectionTitle: form.blogSectionTitle,
          blogSectionIcon: form.blogSectionIcon,
          blogViewAllText: form.blogViewAllText,
          blogEmptyTitle: form.blogEmptyTitle,
          notificationsTitle: form.notificationsTitle,
          notificationsIcon: form.notificationsIcon,
          notificationsEmptyTitle: form.notificationsEmptyTitle,
          notificationsEmptySubtitle: form.notificationsEmptySubtitle,
          whatsNewTitle: form.whatsNewTitle,
          whatsNewIcon: form.whatsNewIcon,
          whatsNewSubtitle: form.whatsNewSubtitle,
          connectTitle: form.connectTitle,
          connectIcon: form.connectIcon,
          connectSubtitle: form.connectSubtitle,
          socialLinks: form.socialLinks.map((item, index) => ({ ...item, order: index })),
          resources: form.resources.map((item, index) => ({ ...item, order: index })),
          isPublished: form.isPublished,
        },
      };
      const res = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/dashboard`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        toastSuccess('Dashboard portal content saved');
        fetchContent();
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Restore preloaded dashboard content? This overwrites your edits.')) return;
    setResetting(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/dashboard/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        toastSuccess('Preloaded defaults restored');
        fetchContent();
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to reset');
    } finally {
      setResetting(false);
    }
  };

  const tabs = [
    { id: 'header', label: 'Welcome Banner' },
    { id: 'sections', label: 'Page Sections' },
    { id: 'resources', label: "What's New Cards" },
    { id: 'connect', label: 'Connect / Social' },
  ];

  const SectionEditor = ({ title, description, children }) => (
    <div className="rounded-2xl border border-gray-200 dark:border-dark-600 p-5 space-y-4 bg-gray-50/50 dark:bg-dark-900/30">
      <div>
        <h3 className="font-bold text-gray-900 dark:text-white">{title}</h3>
        {description && <p className="text-xs text-gray-500 mt-1">{description}</p>}
      </div>
      {children}
    </div>
  );

  return (
    <PageLayout>
      <PageHeader
        title="Dashboard — Portal CMS"
        description="Edit User Portal dashboard titles, resource cards, files, and social links."
        icon={LuLayoutDashboard}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <button type="button" onClick={() => navigate('/portal-cms')} className="px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-600 text-sm">
              Back
            </button>
            <button type="button" onClick={handleReset} disabled={resetting || saving} className="px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-600 text-sm flex items-center gap-2">
              <FaUndo className="w-3.5 h-3.5" /> Reload Defaults
            </button>
            <button type="button" onClick={handleSave} disabled={saving} className="btn-premium flex items-center gap-2">
              <FaSave className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        }
      />

      {loading ? (
        <SkeletonLoader variant="page" />
      ) : (
        <div className="space-y-6">
          <div className="flex items-center justify-between bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700 p-4">
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
              <input type="checkbox" checked={form.isPublished} onChange={(e) => updateField('isPublished', e.target.checked)} className="rounded" />
              Published on User Portal
            </label>
            <div className="flex gap-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-primary-600 text-white' : 'bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-300'}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {activeTab === 'header' && (
            <div className="bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700 p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs font-semibold uppercase text-gray-500">Welcome Title</label>
                  <input className="input-modern mt-1" value={form.welcomeTitle} onChange={(e) => updateField('welcomeTitle', e.target.value)} placeholder="Welcome back" />
                  <p className="text-xs text-gray-500 mt-1">Text before the user&apos;s name — e.g. &quot;Welcome back, John!&quot;</p>
                </div>
                <PortalIconPicker value={form.welcomeIcon} onChange={(icon) => updateField('welcomeIcon', icon)} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-gray-500">Welcome Subtitle</label>
                <textarea className="input-modern mt-1 min-h-[80px]" value={form.welcomeSubtitle} onChange={(e) => updateField('welcomeSubtitle', e.target.value)} placeholder="Stay updated with the latest news, blog posts, and notifications." />
              </div>
            </div>
          )}

          {activeTab === 'sections' && (
            <div className="space-y-5">
              <SectionEditor title="Latest Blog Posts" description="Left column — blog list with title icon and View All link.">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold uppercase text-gray-500">Section Title</label>
                    <input className="input-modern mt-1" value={form.blogSectionTitle} onChange={(e) => updateField('blogSectionTitle', e.target.value)} placeholder="Latest Blog Posts" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase text-gray-500">View All Link Text</label>
                    <input className="input-modern mt-1" value={form.blogViewAllText} onChange={(e) => updateField('blogViewAllText', e.target.value)} placeholder="View All" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <PortalIconPicker value={form.blogSectionIcon} onChange={(icon) => updateField('blogSectionIcon', icon)} />
                  <div>
                    <label className="text-xs font-semibold uppercase text-gray-500">Empty State Title</label>
                    <input className="input-modern mt-1" value={form.blogEmptyTitle} onChange={(e) => updateField('blogEmptyTitle', e.target.value)} placeholder="No blog posts available" />
                  </div>
                </div>
              </SectionEditor>

              <SectionEditor title="Notifications" description="Right column — live notifications feed (content comes from system).">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold uppercase text-gray-500">Section Title</label>
                    <input className="input-modern mt-1" value={form.notificationsTitle} onChange={(e) => updateField('notificationsTitle', e.target.value)} placeholder="Notifications" />
                  </div>
                  <PortalIconPicker value={form.notificationsIcon} onChange={(icon) => updateField('notificationsIcon', icon)} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold uppercase text-gray-500">Empty State Title</label>
                    <input className="input-modern mt-1" value={form.notificationsEmptyTitle} onChange={(e) => updateField('notificationsEmptyTitle', e.target.value)} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase text-gray-500">Empty State Subtitle</label>
                    <input className="input-modern mt-1" value={form.notificationsEmptySubtitle} onChange={(e) => updateField('notificationsEmptySubtitle', e.target.value)} />
                  </div>
                </div>
              </SectionEditor>

              <SectionEditor title="What's New" description="Resource cards section — title, subtitle, and header icon.">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold uppercase text-gray-500">Section Title</label>
                    <input className="input-modern mt-1" value={form.whatsNewTitle} onChange={(e) => updateField('whatsNewTitle', e.target.value)} placeholder="What's New" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase text-gray-500">Section Subtitle</label>
                    <input className="input-modern mt-1" value={form.whatsNewSubtitle} onChange={(e) => updateField('whatsNewSubtitle', e.target.value)} />
                  </div>
                </div>
                <PortalIconPicker value={form.whatsNewIcon} onChange={(icon) => updateField('whatsNewIcon', icon)} />
              </SectionEditor>

              <SectionEditor title="Connect With Us" description="Social links section — title, subtitle, and header icon.">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold uppercase text-gray-500">Section Title</label>
                    <input className="input-modern mt-1" value={form.connectTitle} onChange={(e) => updateField('connectTitle', e.target.value)} placeholder="Connect With Us" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase text-gray-500">Section Subtitle</label>
                    <input className="input-modern mt-1" value={form.connectSubtitle} onChange={(e) => updateField('connectSubtitle', e.target.value)} />
                  </div>
                </div>
                <PortalIconPicker value={form.connectIcon} onChange={(icon) => updateField('connectIcon', icon)} />
              </SectionEditor>
            </div>
          )}

          {activeTab === 'resources' && (
            <div className="bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700 p-6 space-y-5">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Section title and icon are under <strong>Page Sections → What&apos;s New</strong>. Edit individual resource cards below.
              </p>

              <div className="flex items-center justify-between">
                <h3 className="font-bold text-gray-900 dark:text-white">Resource Cards</h3>
                <button type="button" onClick={addResource} className="flex items-center gap-2 text-sm text-primary-600 font-medium">
                  <FaPlus className="w-3.5 h-3.5" /> Add Section
                </button>
              </div>

              <div className="space-y-4">
                {form.resources.map((resource, index) => (
                  <div key={`resource-${index}`} className="rounded-2xl border border-gray-200 dark:border-dark-600 p-5 space-y-4 bg-gray-50/50 dark:bg-dark-900/30">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold">Card {index + 1}</span>
                      <div className="flex items-center gap-1">
                        <button type="button" onClick={() => moveResource(index, -1)} disabled={index === 0} className="p-2 rounded-lg border disabled:opacity-40"><FaArrowUp className="w-3 h-3" /></button>
                        <button type="button" onClick={() => moveResource(index, 1)} disabled={index === form.resources.length - 1} className="p-2 rounded-lg border disabled:opacity-40"><FaArrowDown className="w-3 h-3" /></button>
                        <button type="button" onClick={() => removeResource(index)} className="p-2 rounded-lg text-red-500 hover:bg-red-50"><FaTrash className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-semibold uppercase text-gray-500">Title</label>
                        <input className="input-modern mt-1" value={resource.title} onChange={(e) => updateResource(index, 'title', e.target.value)} />
                      </div>
                      <div>
                        <label className="text-xs font-semibold uppercase text-gray-500">Link Type</label>
                        <select className="input-modern mt-1" value={resource.linkType} onChange={(e) => updateResource(index, 'linkType', e.target.value)}>
                          {LINK_TYPES.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="text-xs font-semibold uppercase text-gray-500">Description</label>
                      <textarea className="input-modern mt-1 min-h-[70px]" value={resource.description} onChange={(e) => updateResource(index, 'description', e.target.value)} />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <PortalIconPicker
                        value={resource.icon}
                        onChange={(icon) => updateResource(index, 'icon', icon)}
                      />
                      <PortalColorPicker
                        value={resource.color}
                        onChange={(color) => updateResource(index, 'color', color)}
                      />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 text-sm cursor-pointer pb-2">
                        <input type="checkbox" checked={resource.isActive !== false} onChange={(e) => updateResource(index, 'isActive', e.target.checked)} />
                        Active
                      </label>
                    </div>

                    {resource.linkType === 'file' && (
                      <div className="space-y-3 p-4 rounded-xl bg-white dark:bg-dark-800 border border-dashed border-gray-200 dark:border-dark-600">
                        <div className="flex flex-wrap items-center gap-3">
                          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 text-sm font-medium cursor-pointer">
                            <FaUpload className="w-3.5 h-3.5" />
                            {uploadingIndex === index ? 'Uploading...' : 'Upload File'}
                            <input type="file" className="hidden" onChange={(e) => uploadResourceFile(index, e.target.files?.[0])} />
                          </label>
                          {resource.fileUrl && (
                            <button type="button" onClick={() => updateResource(index, 'fileUrl', '')} className="text-xs text-red-500 font-medium">
                              Remove file
                            </button>
                          )}
                        </div>
                        <input className="input-modern" value={resource.fileUrl} onChange={(e) => updateResource(index, 'fileUrl', e.target.value)} placeholder="File URL e.g. /uploads/portal/guide.pdf" />
                        <input className="input-modern" value={resource.downloadName} onChange={(e) => updateResource(index, 'downloadName', e.target.value)} placeholder="Download filename" />
                      </div>
                    )}

                    {resource.linkType !== 'file' && (
                      <input className="input-modern" value={resource.linkUrl} onChange={(e) => updateResource(index, 'linkUrl', e.target.value)} placeholder={resource.linkType === 'internal' ? '/help' : 'https://...'} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'connect' && (
            <div className="space-y-6">
              <p className="text-sm text-gray-500 dark:text-gray-400 px-1">
                Section title and icon are under <strong>Page Sections → Connect With Us</strong>. Add or edit social platforms below — change icon, brand color, URL, and text for each.
              </p>

              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold uppercase text-gray-500 mr-1">Quick add:</span>
                {SOCIAL_PRESETS.map((preset) => (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => addSocialLink(preset)}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 dark:border-dark-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 text-primary-700 dark:text-primary-300"
                  >
                    + {preset.label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => addSocialLink()}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border border-dashed border-gray-300 dark:border-dark-500 text-gray-600 dark:text-gray-400"
                >
                  + Custom Platform
                </button>
              </div>

              <div className="space-y-4">
                {form.socialLinks.map((social, index) => (
                  <div key={`social-${index}`} className="bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700 p-6 space-y-4">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-bold text-gray-900 dark:text-white">
                        Platform {index + 1}{social.title ? ` — ${social.title}` : ''}
                      </span>
                      <div className="flex items-center gap-1">
                        <button type="button" onClick={() => moveSocialLink(index, -1)} disabled={index === 0} className="p-2 rounded-lg border disabled:opacity-40"><FaArrowUp className="w-3 h-3" /></button>
                        <button type="button" onClick={() => moveSocialLink(index, 1)} disabled={index === form.socialLinks.length - 1} className="p-2 rounded-lg border disabled:opacity-40"><FaArrowDown className="w-3 h-3" /></button>
                        <button type="button" onClick={() => removeSocialLink(index)} className="p-2 rounded-lg text-red-500 hover:bg-red-50"><FaTrash className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <PortalIconPicker value={social.icon} onChange={(icon) => updateSocial(index, 'icon', icon)} />
                      <div>
                        <label className="text-xs font-semibold uppercase text-gray-500">Brand Color</label>
                        <div className="flex items-center gap-3 mt-1">
                          <input type="color" value={social.brandColor || '#3b82f6'} onChange={(e) => updateSocial(index, 'brandColor', e.target.value)} className="w-10 h-10 rounded-lg border cursor-pointer" />
                          <input className="input-modern flex-1" value={social.brandColor || ''} onChange={(e) => updateSocial(index, 'brandColor', e.target.value)} placeholder="#0077B5" />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-semibold uppercase text-gray-500">Title</label>
                        <input className="input-modern mt-1" value={social.title} onChange={(e) => updateSocial(index, 'title', e.target.value)} placeholder="LinkedIn" />
                      </div>
                      <div>
                        <label className="text-xs font-semibold uppercase text-gray-500">Subtitle</label>
                        <input className="input-modern mt-1" value={social.subtitle} onChange={(e) => updateSocial(index, 'subtitle', e.target.value)} placeholder="Follow us for updates" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-semibold uppercase text-gray-500">Profile URL</label>
                        <input className="input-modern mt-1" value={social.url} onChange={(e) => updateSocial(index, 'url', e.target.value)} placeholder="https://..." />
                      </div>
                      <div>
                        <label className="text-xs font-semibold uppercase text-gray-500">Button Text</label>
                        <input className="input-modern mt-1" value={social.buttonText} onChange={(e) => updateSocial(index, 'buttonText', e.target.value)} placeholder="Follow on LinkedIn" />
                      </div>
                    </div>

                    <div>
                      <label className="text-xs font-semibold uppercase text-gray-500">Description</label>
                      <textarea className="input-modern mt-1 min-h-[70px]" value={social.description} onChange={(e) => updateSocial(index, 'description', e.target.value)} placeholder="Short description shown on the card" />
                    </div>

                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={social.isActive !== false} onChange={(e) => updateSocial(index, 'isActive', e.target.checked)} />
                      Show on User Portal
                    </label>
                  </div>
                ))}

                {form.socialLinks.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-8 border border-dashed rounded-2xl">
                    No social platforms yet. Use Quick add above or Reload Defaults.
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <button type="button" onClick={handleSave} disabled={saving} className="btn-premium flex items-center gap-2">
              <FaSave className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default DashboardPortalCMS;
